import { useEffect, useState } from 'react'
import { useLocation, useNavigate, Location } from 'react-router-dom'
import { clearAuthToken, getAuthSessionInfo } from 'modules/auth/api/authService'
import { logoutAzureSession } from 'modules/auth/api/azureAuth'
import { SiteSelector, type Site as TenantSite } from 'shared/components'
import { fetchUserMenus } from 'modules/core/api/menuAccessService'
import type { LoginRoleDto, LoginMenuDto } from 'modules/auth/api/authService'
import { getPendingApprovals } from 'modules/permit'

type NavigationState = {
  user?: any
  module?: 'E-PTW' | 'ADMIN'
}

const normalizeGuid = (value: unknown): string | null => {
  if (typeof value !== 'string') {
    return null
  }
  const trimmed = value.trim()
  if (!trimmed) {
    return null
  }
  return /^[0-9a-fA-F-]{36}$/.test(trimmed) ? trimmed.toLowerCase() : null
}

const resolveAccessibleLocationIds = (user?: Record<string, any>, roles?: Array<Record<string, any>> | null) => {
  const results = new Set<string>()

  ;[
    user?.locationIds,
    user?.location_ids,
    user?.workAreaIds,
    user?.work_area_ids,
    user?.accessibleLocationIds,
    user?.accessible_location_ids,
    user?.locations,
  ].forEach((candidate) => {
    if (!Array.isArray(candidate)) return
    candidate.forEach((item) => {
      const normalized = normalizeGuid(typeof item === 'string' ? item : item?.locationId ?? item?.id)
      if (normalized) {
        results.add(normalized)
      }
    })
  })

  ;(roles ?? []).forEach((role) => {
    const normalized =
      normalizeGuid(role?.departmentId) ??
      normalizeGuid(role?.department_id) ??
      normalizeGuid(role?.locationId) ??
      normalizeGuid(role?.location_id)
    if (normalized) {
      results.add(normalized)
    }
  })

  return Array.from(results)
}

const getState = (location: Location): NavigationState => {
  const state = location.state as NavigationState | null
  if (!state || typeof state !== 'object') {
    return {}
  }
  return state
}

export function SiteSelectionPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const { user, module } = getState(location)
  const [isOpen, setIsOpen] = useState(true)

  // Guard against direct navigation without proper state
  useEffect(() => {
    if (!user) {
      navigate('/modules', { replace: true })
    }
  }, [user, navigate])

  const handleSelectSite = async (site: TenantSite) => {
    if (!user) {
      navigate('/login', { replace: true })
      return
    }

    const matchedTenant = Array.isArray(user.tenants)
      ? user.tenants.find((t: any) => t.tenantId === site.id)
      : undefined

    let tenantMenus = matchedTenant?.menus ?? []
    let tenantRoles = matchedTenant?.roles ?? []

    // ถ้าไม่มีเมนูจากการ login ให้ดึงตาม userId + tenantId
    if ((!tenantMenus || tenantMenus.length === 0) && user.userId) {
      try {
        tenantMenus = await fetchUserMenus(user.userId, site.id)
      } catch (err) {
        console.error('Failed to load menus for tenant', err)
        tenantMenus = []
      }
    }

    // ถ้าไม่มี role จาก login ให้สร้างจากเมนู (ใช้ roleId ที่แนบมากับเมนู)
    if ((!tenantRoles || tenantRoles.length === 0) && tenantMenus?.length) {
      const roleIds = Array.from(
        new Set(
          tenantMenus
            .map((m: LoginMenuDto) => m as any)
            .map((m: any) => m.roleId ?? m.role_id)
            .filter(Boolean)
            .map((id: any) => String(id))
        )
      )
      tenantRoles = roleIds.map((roleId) => ({ roleId })) as LoginRoleDto[]
    }

    const sanitizedSite: TenantSite = {
      id: site.id,
      name: site.name,
      description: site.description,
      location: site.location,
      activeUsers: site.activeUsers
    }

    try {
      sessionStorage.setItem('eptw:selectedSiteId', sanitizedSite.id)
    } catch {
      // ignore storage errors
    }

    const nextState = {
      user: {
        ...user,
        role: tenantRoles?.[0]?.displayName ?? user.role,
        fullName: user.fullName ?? user.username,
      },
      module: module ?? 'E-PTW',
      site: sanitizedSite,
      menus: tenantMenus,
      roles: tenantRoles,
      tenantName: sanitizedSite.name,
    }

    const currentUserIdCandidates = [
      user.id,
      user.user_id,
      user.userId,
      user.emp_id,
      user.empId,
      user.employee_id,
      user.employeeId,
    ]
    const currentUserId =
      currentUserIdCandidates
        .map((candidate) => normalizeGuid(candidate))
        .find(Boolean) ?? null

    let landingPath = '/permit-index'
    try {
      const pendingApprovals = await getPendingApprovals({
        tenantId: sanitizedSite.id,
        pageNumber: 1,
        pageSize: 1,
        assignedToUserId: currentUserId ?? undefined,
      })
      if ((pendingApprovals.totalItems ?? pendingApprovals.items?.length ?? 0) > 0) {
        landingPath = '/permit-approvals'
      }
    } catch (error) {
      console.error('Failed to resolve landing page from pending approvals', error)
    }

    navigate(landingPath, {
      replace: true,
      state: nextState,
    })
  }

  if (!user) {
    return null
  }

  const handleBackToLogin = async () => {
    const authSession = getAuthSessionInfo()
    const isContractorAzure =
      authSession?.loginMethod === 'AZURE' &&
      authSession?.userType?.trim().toUpperCase() === 'CONTRACTOR'

    clearAuthToken()

    if (isContractorAzure) {
      try {
        await logoutAzureSession('CONTRACTOR', '/login/contractor')
        return
      } catch (error) {
        console.error('Failed to logout contractor Azure session', error)
      }
    }

    navigate('/login', { replace: true })
  }

  const sites: TenantSite[] | undefined = Array.isArray(user.tenants)
    ? user.tenants.map((t: any) => ({
        id: t.tenantId,
        name: t.tenantName ?? t.tenantId,
        description: '',
        location: '',
        activeUsers: 0
      }))
    : undefined

  const userId = [user?.userId, user?.id, user?.user_id]
    .map((v) => (typeof v === 'string' && v.trim() ? v.trim() : null))
    .find(Boolean) ?? undefined

  return (
    <SiteSelector
      isOpen={isOpen}
      sites={sites}
      userId={userId}
      onBack={() => {
        void handleBackToLogin()
      }}
      onSelectSite={(site: TenantSite) => {
        setIsOpen(false)
        void handleSelectSite(site)
      }}
    />
  )
}
